import { useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import { setAnalyticsButtonClick } from "../../../helpers/analyticsHelpers";
import { getNavigationItems } from "../constants/navigationConfig";
import { NAVIGATION_ITEM_IDS } from "../constants/navigationConfig";

/**
 * Hook customizado para gerenciar navegação
 * @param {boolean} isUserAuthenticated - Se o usuário está autenticado
 * @param {boolean} hasNewReward - Se há nova recompensa disponível
 * @param {boolean} isPatient - Se o usuário é do tipo "paciente vinculado"
 * @param {boolean} isFree - Se o usuário é do tipo "free"
 * @param {boolean} isTrial - Se o usuário é do tipo "trial"
 * @returns {Object} Navegação handlers e estado
 */
export const useNavigation = (
  isUserAuthenticated,
  hasNewReward,
  isPatient = false,
  isFree,
  isTrial,
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  // Itens de navegação filtrados
  const navigationItems = useMemo(() => {
    let items = getNavigationItems(isUserAuthenticated, hasNewReward);

    // Remove "Ajuda IA" se o usuário for do tipo "patient"
    if (isPatient) {
      items = items.filter((item) => item.id !== NAVIGATION_ITEM_IDS.HELP);
    }

    // Inversões no mobile/tablet
    if (!isDesktop) {
      const progressIndex = items.findIndex(
        (item) => item.id === NAVIGATION_ITEM_IDS.PROGRESS,
      );
      const buyIndex = items.findIndex(
        (item) => item.id === NAVIGATION_ITEM_IDS.BUY,
      );

      if (progressIndex !== -1 && buyIndex !== -1) {
        const progressItem = items[progressIndex];
        const buyItem = items[buyIndex];

        // Para isFree ou isTrial: Comprar na barra, Progresso no Mais
        if (isFree || isTrial) {
          items = items.filter(
            (item) =>
              item.id !== NAVIGATION_ITEM_IDS.PROGRESS &&
              item.id !== NAVIGATION_ITEM_IDS.BUY,
          );
          items.splice(progressIndex, 0, buyItem);
          items.splice(buyIndex, 0, progressItem);
        }
        // Para patient_free: Progresso na barra, Comprar no Mais
        else if (isPatient && isFree) {
          items = items.filter(
            (item) =>
              item.id !== NAVIGATION_ITEM_IDS.PROGRESS &&
              item.id !== NAVIGATION_ITEM_IDS.BUY,
          );
          items.splice(buyIndex, 0, progressItem);
          items.splice(progressIndex, 0, buyItem);
        }
      }
    }

    return items;
  }, [
    isUserAuthenticated,
    hasNewReward,
    isPatient,
    isFree,
    isTrial,
    isDesktop,
  ]);

  // Handler para navegação com analytics
  const handleNavigation = useCallback(
    (item, onComplete) => {
      if (item.analytics) {
        setAnalyticsButtonClick({
          target: item.analytics,
        });
      }

      const isCurrentRoute =
        location.pathname === item.route ||
        (item.route !== "/" && location.pathname.startsWith(`${item.route}/`));

      if (!isCurrentRoute) {
        navigate(`${item.route}${location.search}`)
      }
      if (onComplete) {
        onComplete();
      }
    },
    [navigate, location.pathname],
  );

  // Determina o índice do item ativo
  const activeIndex = useMemo(() => {
    const pathname = location.pathname;
    const index = navigationItems.findIndex((item) => {
      if (!item.route) return false;
      return pathname === item.route || pathname.startsWith(`${item.route}/`);
    });
    return index !== -1 ? index : 0;
  }, [location.pathname, navigationItems]);

  return {
    navigationItems,
    activeIndex,
    handleNavigation,
  };
};
